import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { api, apiErr } from "@/lib/api";
import { Eye, EyeOff, Users } from "lucide-react";
import Footer from "@/components/Footer";

/**
 * Login — Gestionale Presenze (Brand Book Gruppo Impresa Suite v1.0)
 *
 * Layout:
 *   - Sfondo bianco puro con sfumatura pastello verdissima
 *   - Card bianca centrata con angoli 24px, ombra morbida
 *   - Tre cerchi corporate (#E74C3C / #95A5A6 / #F2C94C)
 *   - Lock-up brand "GRUPPO IMPRESA SUITE" (Montserrat 800, icona barra dorata)
 *   - Divider fine
 *   - Icona persone/presenze + nome modulo "Gestionale Presenze" (Montserrat 600, #4CAF50)
 *   - Campi email + password (Inter)
 *   - Password dimenticata? in #4CAF50
 *   - Bottone ACCEDI full-width #4CAF50
 *   - Fascia decorativa a onda in #4CAF50 nella parte bassa della card
 *   - Footer istituzionale fuori dalla card
 */

const MODULE_GREEN = "#4CAF50";
const MODULE_GREEN_DARK = "#3F9E43";
const DOT_RED = "#E74C3C";
const DOT_GRAY = "#95A5A6";
const DOT_GOLD = "#F2C94C";

/** Icona SVG "grafico a barre" dorato — corrisponde al logo Gruppo Impresa Suite del brand book */
function BarChartLogo({ size = 44 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <rect x="6"  y="26" width="7" height="16" rx="1.2" fill={DOT_GOLD} />
      <rect x="15" y="18" width="7" height="24" rx="1.2" fill={DOT_GOLD} />
      <rect x="24" y="10" width="7" height="32" rx="1.2" fill={DOT_GOLD} />
      <rect x="33" y="20" width="7" height="22" rx="1.2" fill={DOT_GOLD} />
    </svg>
  );
}

/** Fascia decorativa a onda in fondo alla card */
function BottomWave({ color = MODULE_GREEN }) {
  return (
    <svg
      viewBox="0 0 600 80" preserveAspectRatio="none"
      className="absolute bottom-0 left-0 w-full h-16 pointer-events-none select-none"
      aria-hidden="true"
    >
      <path d="M0 60 C 120 20, 240 100, 360 50 S 540 20, 600 55 L 600 80 L 0 80 Z"
            fill={color} opacity="0.9" />
      <path d="M0 68 C 130 40, 250 90, 380 60 S 540 40, 600 70 L 600 80 L 0 80 Z"
            fill={color} opacity="0.55" />
    </svg>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // D2-A: messaggio "sessione terminata per inattività" quando arriviamo con ?reason=idle_timeout
  const [reasonMsg, setReasonMsg] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      let r = params.get("reason");
      if (!r) {
        // D2-A · backup via sessionStorage per casi di race-condition col redirect
        try {
          r = sessionStorage.getItem("gi_login_reason");
          if (r) sessionStorage.removeItem("gi_login_reason");
        } catch { /* noop */ }
      }
      if (r === "idle_timeout") return "Sessione terminata per inattività. Accedi nuovamente.";
      return "";
    } catch { return ""; }
  });

  const [branding, setBranding] = useState(null);
  const [brandingErr, setBrandingErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!slug) { setBranding(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/public/portal/${slug}`);
        if (!cancelled) setBranding(data);
      } catch {
        if (!cancelled) { setBranding(null); setBrandingErr(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // C3-B.2.7: campo unico "Nome utente" — accetta username o email.
  // Precompila SOLO l'ultimo username usato con successo, mai la password.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("presenze_last_username")
                    || localStorage.getItem("presenze_last_email");
      if (saved) setEmail(saved);
    } catch { /* ignore */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      try {
        // Memorizza sempre e soltanto il nome utente dell'ultimo accesso
        // riuscito. La password resta esclusivamente nello stato volatile
        // della pagina e non viene mai persistita.
        localStorage.setItem("presenze_last_username", email.trim());
        localStorage.removeItem("presenze_last_email");
      } catch { /* ignore */ }
      navigate("/");
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col"
      style={{
        fontFamily: "'Inter', sans-serif",
        background: "linear-gradient(180deg, #FFFFFF 0%, #F4FBF5 45%, #EAF7EC 100%)",
      }}
      data-testid="login-page"
    >
      {/* Micro-decorazione dot brand in alto a sinistra dello sfondo — molto discreta */}
      <div aria-hidden="true" className="pointer-events-none fixed top-6 left-8 flex items-center gap-1.5 opacity-30">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: DOT_RED }} />
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: DOT_GRAY }} />
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: DOT_GOLD }} />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div
          className="relative w-full max-w-[420px] bg-white rounded-3xl overflow-hidden"
          style={{
            boxShadow: "0 20px 45px -18px rgba(15,50,25,0.18), 0 4px 12px -4px rgba(15,50,25,0.08)",
          }}
          data-testid="login-card"
        >
          <div className="relative z-10 px-8 pt-8 pb-14">
            {/* 3 cerchi corporate */}
            <div className="flex items-center gap-2.5 mb-6" data-testid="brand-dots">
              <span className="w-3.5 h-3.5 rounded-full" style={{ background: DOT_RED }} />
              <span className="w-3.5 h-3.5 rounded-full" style={{ background: DOT_GRAY }} />
              <span className="w-3.5 h-3.5 rounded-full" style={{ background: DOT_GOLD }} />
            </div>

            {/* Lock-up Suite */}
            <div className="flex items-center gap-3">
              <BarChartLogo size={44} />
              <div className="leading-none">
                <div
                  className="text-[19px] font-extrabold tracking-tight text-suite-neutral-800"
                  style={{ fontFamily: "Montserrat, sans-serif", letterSpacing: "0.01em" }}
                >
                  GRUPPO IMPRESA
                </div>
                <div
                  className="text-[13px] font-semibold text-suite-neutral-500 mt-1"
                  style={{ fontFamily: "Montserrat, sans-serif", letterSpacing: "0.55em" }}
                >
                  SUITE
                </div>
              </div>
            </div>

            {/* Divider fine */}
            <div className="my-6 h-px bg-gradient-to-r from-transparent via-suite-neutral-200 to-transparent" />

            {/* Modulo Presenze */}
            <div className="flex items-center gap-2.5" data-testid="module-title">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: `${MODULE_GREEN}18`, border: `1px solid ${MODULE_GREEN}33` }}
              >
                <Users size={18} strokeWidth={2} style={{ color: MODULE_GREEN }} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-suite-neutral-400">
                  Modulo
                </div>
                <div
                  className="text-[19px] font-semibold leading-tight"
                  style={{ color: MODULE_GREEN, fontFamily: "Montserrat, sans-serif" }}
                >
                  Gestionale Presenze
                </div>
              </div>
            </div>

            {/* Portale aziendale (se slug) */}
            {branding?.name && (
              <div
                className="mt-4 text-[11px] px-3 py-2 rounded-lg border"
                style={{ borderColor: `${MODULE_GREEN}33`, background: `${MODULE_GREEN}0d` }}
                data-testid="brand-company-badge"
              >
                <span className="text-suite-neutral-500">Portale aziendale · </span>
                <span className="font-semibold text-suite-neutral-800">{branding.name}</span>
              </div>
            )}
            {brandingErr && slug && (
              <div className="mt-4 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                   data-testid="branding-not-found">
                Portale &quot;{slug}&quot; non trovato — usa la schermata generale sottostante.
              </div>
            )}

            {/* Form */}
            <form onSubmit={submit} className="space-y-4 mt-7" data-testid="login-form">
              <div className="space-y-1.5">
                <label htmlFor="username" className="text-[11px] font-semibold text-suite-neutral-600 uppercase tracking-wider">
                  Nome utente
                </label>
                <Input
                  id="username" type="text" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome.cognome"
                  autoComplete="username"
                  required
                  data-testid="login-username-input"
                  className="h-11 rounded-lg border-suite-neutral-200 focus-visible:ring-0 focus-visible:border-[color:var(--focus)] transition-colors"
                  style={{ "--focus": MODULE_GREEN }}
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="password" className="text-[11px] font-semibold text-suite-neutral-600 uppercase tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password" type={showPassword ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Inserisci la password"
                    autoComplete="off"
                    required
                    data-testid="login-password-input"
                    className="h-11 rounded-lg pr-11 border-suite-neutral-200 focus-visible:ring-0 focus-visible:border-[color:var(--focus)] transition-colors"
                    style={{ "--focus": MODULE_GREEN }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-suite-neutral-400 hover:text-suite-neutral-700 focus:outline-none rounded-md"
                    data-testid="login-toggle-password"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Recupero password */}
              <div className="flex items-center justify-end pt-1">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-[13px] font-medium hover:underline underline-offset-2 transition-colors"
                  style={{ color: MODULE_GREEN }}
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </button>
              </div>

              {reasonMsg && !err && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                     data-testid="login-reason-msg">
                  {reasonMsg}
                </div>
              )}

              {err && (
                <div className="text-sm text-suite-error bg-suite-error-light/60 border border-suite-error-border rounded-lg px-3 py-2"
                     data-testid="login-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-12 rounded-lg text-white font-semibold uppercase tracking-[0.15em] transition-all disabled:opacity-70 disabled:cursor-not-allowed active:translate-y-[1px]"
                style={{
                  background: MODULE_GREEN,
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "13px",
                  boxShadow: `0 6px 16px -6px ${MODULE_GREEN}66`,
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = MODULE_GREEN_DARK; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = MODULE_GREEN; }}
              >
                {loading ? "Accesso in corso…" : "Accedi"}
              </button>

              <p className="text-[11px] text-suite-neutral-400 text-center leading-relaxed pt-1">
                Se non hai ancora le credenziali, contatta l&apos;amministratore del portale.
              </p>
            </form>
          </div>

          {/* Fascia decorativa a onda in fondo alla card */}
          <BottomWave color={MODULE_GREEN} />
        </div>
      </div>

      <Footer />
    </div>
  );
}
